import React, { useState, forwardRef, useEffect } from "react";
import {
  Paper,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slide,
  Grid,
  TextField,
  Typography,
  FormControl,
  MenuItem,
  FormHelperText,
  InputLabel,
  Select,
  IconButton,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { formatDate } from "@fullcalendar/core";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { createHoliday, getYearWiseHolidays } from "../../../api/holidays"; // Import your API function
import { useDispatch, useSelector } from "react-redux";
import { handleAxiosError } from "../../../config/action-config";
import BetterControlledTable from "../../../component/BetterControlledTable";
import { CalenderTable as tableDef } from "../../../config/grid-columns-defs";
import { Controller, useForm } from "react-hook-form";
import { showAlert } from "../../../actions/alert";
import { useNavigate } from "react-router-dom";

export default function HolidayCalendar() {
  const [holidays, setHolidays] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [tableRefreshCount, setTableRefreshCount] = useState(0);
  const dialogAction = useSelector((state) => state.dialog);
  let obj = tableDef.find((o) => o.headerName === "Action");
  const [customDate, setCustomDate] = useState("");
  const [dayName, setDayName] = useState("");
  addAdditionalColumn(obj, dispatch, user);

  useEffect(() => {
    if (!dialogAction.type) return;
    switch (dialogAction.type) {
      case "VIEW":
        dispatch({ type: "" });
        navigate(`${dialogAction.data.id}`);
        break;
      default:
        break;
    }
  }, [dialogAction]);
  async function getRows(limit, offset) {
    try {
      const { data } = await getYearWiseHolidays(
        { year: selectedYear, limit, offset },
        user.token
      );
      return {
        rows: data.data.leaves,
        total: data.data.total,
        error: false,
      };
    } catch (error) {
      handleAxiosError(error, dispatch);
      return { error: true };
    } finally {
      // setLoading(false);
    }
  }
  function refreshTable(e) {
    e?.preventDefault();
    setTableRefreshCount((prev) => prev + 1);
  }
  useEffect(() => {
    refreshTable();
  }, [selectedYear]);
  useEffect(() => {
    // Fetch holidays for the selected year
    if (user.token) {
      getCalenderData();
    }
  }, [selectedYear, user.token]);
  async function getCalenderData() {
    getYearWiseHolidays({ year: selectedYear }, user.token)
      .then(({ data }) => {
        const formattedHolidays = data.data.leaves.map((holiday) => ({
          title: holiday.occasion,
          start: holiday.date_of_holiday,
          id: holiday.id,
          allDay: true,
        }));
        setHolidays(formattedHolidays);
      })
      .catch((error) => {
        handleAxiosError(error, dispatch);
      });
  }
  // Callback function when calendar dates change
  function handleDatesSet(dateInfo) {
    const newYear = dateInfo.view.currentStart.getFullYear();
    setSelectedYear(newYear);
  }
  const onDateClick = (dateInfo) => {
    if (user.role === "Admin") {
      const eventsForDate = holidays.filter((event) => {
        return (
          new Date(event.start).toDateString() === dateInfo.date.toDateString()
        );
      });
      if (eventsForDate.length > 0) {
        return;
      }
      const dayName = getDayName(dateInfo.date);
      setDayName(dayName);
      setCustomDate(dateInfo.date);
      setCreateDialogOpen(true);
    }
  };
  const onEventClick = ({ event }) => {
    if (user.role === "Admin") {
      navigate(`${event._def.publicId}`);
    }
  };
  return (
    <>
      <Box className="pmt-container-box" sx={{ pt: 1, pb: 1 }}>
        <Box className="pmt-title-box">
          <Box className="typography-title">
            <Typography variant="h1" className="pmt-headings-h1">
              Holidays
            </Typography>
          </Box>
        </Box>
      </Box>

      <Paper elevation={4} sx={{ p: 2, width: "100%" }}>
        <FullCalendar
          events={holidays}
          datesSet={handleDatesSet}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "dayGridMonth,timeGridWeek,timeGridDay",
          }}
          initialView="dayGridMonth"
          editable={true}
          selectable={true}
          selectMirror={true}
          dayMaxEvents={true}
          eventClick={onEventClick}
          // select={() => {}}
          dateClick={onDateClick}
          height="auto"
        />
      </Paper>

      <Box className="pmt-container-box" sx={{ pt: 2 }}>
        <Box className="pmt-title-box">
          <Box className="typography-title">
            <Typography variant="h1" className="pmt-headings-h1">
              List Of All Holidays
            </Typography>
          </Box>
          {user.role === "Admin" && (
            <Button
              variant="contained"
              style={{ padding: "5px 7px" }}
              onClick={() => {
                setCreateDialogOpen(true);
              }}
              className="blue-button"
            >
              Add
            </Button>
          )}
        </Box>
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="body2" color="error">
            <Box fontStyle="italic">
              **In a year you can opt for only 2 floating leaves subject to your
              manager's approval.
            </Box>
          </Typography>
        </Paper>
        <br />
        <div className="pmt-controll-table">
          <BetterControlledTable
            hasActionButtons={false}
            getTableRows={getRows}
            columns={tableDef}
            perPage={10}
            refreshCounter={tableRefreshCount}
          />
        </div>
      </Box>
      <AddHolidayDialog
        open={createDialogOpen}
        setOpen={setCreateDialogOpen}
        onRefresh={refreshTable}
        customDate={customDate}
        setCustomDate={setCustomDate}
        dayName={dayName}
        setDayName={setDayName}
        refreshCalenderApi={getCalenderData}
        disableEscapeKeyDown
      />
    </>
  );
}
function addAdditionalColumn(obj, dispatch, user) {
  if (user.role === "Admin") {
    if (obj === undefined) {
      tableDef.unshift({
        headerName: "Action",
        field: "actions",
        minWidth: 70,
        resizable: true,
        headerClass: "justify-center",
        cellClass: "justify-center",
        cellRendererFramework: (params) => (
          <>
            <>
              <IconButton
                color="info"
                onClick={() => {
                  dispatch({ type: "VIEW", data: params.data });
                }}
              >
                <VisibilityIcon fontSize="inherit" />
              </IconButton>
            </>
          </>
        ),
      });
    }
  }
}
const emptyNewHolidayData = {
  occasion: "",
  date_of_holiday: "",
  holiday_day: "",
  holiday_type: "",
};
// ... (import statements)

function AddHolidayDialog({
  open,
  setOpen,
  onRefresh,
  customDate,
  setCustomDate,
  dayName,
  setDayName,
  refreshCalenderApi,
}) {
  const user = useSelector((state) => state.user);
  const [data, setData] = useState(emptyNewHolidayData);

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm({ defaultValues: emptyNewHolidayData });

  const dispatch = useDispatch();
  useEffect(() => {
    if (open && customDate === "") {
      setData(emptyNewHolidayData);
    } else if (open && customDate !== "") {
      setValue("date_of_holiday", customDate);
    }
  }, [open]);

  async function onNewHolidaySubmit(data) {
    const newData = { ...data };
    if (customDate) {
      newData.date_of_holiday = createDateAsUTC(customDate);
    } else {
      newData.date_of_holiday = createDateAsUTC(newData.date_of_holiday);
    }
    newData.holiday_day = dayName;

    try {
      const { data } = await createHoliday(newData, user.token);
      dispatch(showAlert(data.msg));
      setOpen(false);
      setCustomDate("");
      onRefresh();
      refreshCalenderApi();
    } catch (err) {
      handleAxiosError(err, dispatch);
      setOpen(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        setOpen(false);
      }}
    >
      <DialogTitle>Holiday</DialogTitle>
      <Box component="form" onSubmit={onNewHolidaySubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6} sm={6}>
              <TextField
                name="occasion"
                label="Occasion"
                required
                fullWidth
                inputRef={register({
                  required: "Occasion is required.",
                })}
                error={Boolean(errors.occasion)}
                helperText={errors.occasion?.message}
              />
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <FormControl fullWidth error={Boolean(errors.date_of_holiday)}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Controller
                    render={(props) => (
                      <DesktopDatePicker
                        value={props.value || customDate} // Use props.value or defaultDate if props.value is undefined
                        onChange={(newValue) => {
                          props.onChange(newValue);
                          setCustomDate(newValue);
                          setDayName(getDayName(newValue));
                          // Update defaultDate when the value changes
                        }}
                        label="Date Of Holiday"
                        renderInput={(params) => (
                          <TextField
                            error={Boolean(errors.date_of_holiday)}
                            {...params}
                          />
                        )}
                      />
                    )}
                    rules={{
                      required:
                        customDate === ""
                          ? "Date of holiday is required."
                          : false,
                    }}
                    name="date_of_holiday"
                    control={control}
                  />
                </LocalizationProvider>
                <FormHelperText>
                  {errors.date_of_holiday?.message}
                </FormHelperText>
              </FormControl>
            </Grid>

            {/* <Grid item xs={12} md={6} sm={6}>
              <FormControl
                fullWidth
                required
                error={Boolean(errors.holiday_day)}
              >
                <InputLabel>Holiday Day</InputLabel>
                <Controller
                  render={(props) => (
                    <Select
                      value={props.value}
                      onChange={props.onChange}
                      MenuProps={{ className: "select-box-height" }}
                    >
                      <MenuItem value="" disabled>
                        --select--
                      </MenuItem>
                      <MenuItem value="Monday">Monday</MenuItem>
                      <MenuItem value="Tuesday">Tuesday</MenuItem>
                      <MenuItem value="Wednesday">Wednesday</MenuItem>
                      <MenuItem value="Thursday">Thursday</MenuItem>
                      <MenuItem value="Friday">Friday</MenuItem>
                    </Select>
                  )}
                  name="holiday_day"
                  label="Holiday Day"
                  control={control}
                  rules={{
                    required: "Holiday Day is required.",
                  }}
                />
                <FormHelperText>{errors.holiday_day?.message}</FormHelperText>
              </FormControl>
            </Grid> */}
            <Grid item xs={12} md={6} sm={6}>
              <FormControl
                fullWidth
                required
                error={Boolean(errors.holiday_type)}
              >
                <InputLabel>Holiday Type</InputLabel>
                <Controller
                  render={(props) => (
                    <Select
                      value={props.value}
                      onChange={props.onChange}
                      MenuProps={{ className: "select-box-height" }}
                    >
                      <MenuItem value="" disabled>
                        --select--
                      </MenuItem>
                      <MenuItem value="Regular">Regular</MenuItem>
                      <MenuItem value="Floating">Floating</MenuItem>
                    </Select>
                  )}
                  name="holiday_type"
                  label="Holiday Type"
                  control={control}
                  rules={{
                    required: "Holiday Type is required.",
                  }}
                />
                <FormHelperText>{errors.holiday_type?.message}</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCustomDate("");
              setOpen(false);
            }}
            className="cancel-button"
          >
            Cancel
          </Button>
          <Button
            className="blue-button"
            onClick={handleSubmit(onNewHolidaySubmit)}
            type="submit"
          >
            Submit
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
function createDateAsUTC(date) {
  return new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds()
    )
  );
}
function getDayName(date) {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
  }).format(date);
}
